import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AlertController, IonicModule, ModalController, NavParams } from '@ionic/angular';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { v4 as uuidv4 } from 'uuid';

import { CUSTOM_FIELD_TYPE_SHORT_ANSWER } from 'src/app/constants';
import { environment } from 'src/environments/environment';
import { CustomField } from 'src/models';
import { CustomFieldsService } from 'src/services/custom-fields.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

import { AvatarModule } from '../avatar/avatar.module';
import { ChannelSelectorModule } from '../channel-selector/channel-selector.module';
import { CustomFieldsModule } from '../custom-fields/custom-fields.module';
@Component({
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    FormsModule,
    ChannelSelectorModule,
    CustomFieldsModule,
    NgxTippyModule,
    AvatarModule,
  ],
  selector: 'app-edit-custom-field',
  templateUrl: './edit-custom-field.component.html',
  styleUrls: ['./edit-custom-field.component.scss'],
})
export class EditCustomFieldComponent implements OnInit {
  isDev: boolean;
  isLoading = false;
  id?: string;
  projectId: string;
  field: CustomField;
  constructor(
    navParams: NavParams,

    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private searchablePopoverService: SearchablePopoverService,
    private msgSrvc: MessageService,

    private customFieldsService: CustomFieldsService,
  ) {
    this.id = navParams.get('id');
    this.projectId = navParams.get('projectId');
    this.isDev = environment.environment === 'dev';
  }

  ngOnInit(): void {
    if (this.id) {
      this.loadCustomField();
    } else {
      this.field = {
        label: '',
        description: '',
        type: CUSTOM_FIELD_TYPE_SHORT_ANSWER,
        required: false,
        public: true,
        id: uuidv4(),
        conditions: {
          type: 'simple',
          simple: {},
        },
      };
    }
  }

  async loadCustomField() {
    try {
      const customField = await this.customFieldsService.getById(this.id);
      this.field = customField;
    } catch (error) {
      await this.msgSrvc.showError(error);
    }
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }

  async submit() {
    const cleanedField = {
      ...this.field,
      label: this.field.label?.trim() || `New ${this.field.type} field`,
      options: this.field.externalSelect != null ? null : this.field.options,
      projectId: this.projectId,
      // Custom fields can either be external select OR have options, not both
    };
    try {
      if (this.id) {
        await this.customFieldsService.updatePartial(this.id, cleanedField);
        await this.msgSrvc.show('Custom field updated!');
      } else {
        await this.customFieldsService.create(cleanedField);
        await this.msgSrvc.show('Custom field added!');
      }

      this.dismiss();
    } catch (error) {
      await this.msgSrvc.showError(error);
    } finally {
      this.isLoading = false;
    }
  }

  async delete() {
    const alert = await this.alertCtrl.create({
      header: 'Delete custom field?',
      subHeader: `Please remove this field from any ticket types before deleting. Deletion can't be undone.`,
      buttons: [
        {
          text: 'Nevermind',
        },
        {
          text: 'Delete',
          handler: async () => {
            try {
              this.isLoading = true;
              await this.customFieldsService.delete(this.id);
              this.isLoading = false;
              this.dismiss();
            } catch (e) {
              this.isLoading = false;
              this.msgSrvc.showError(e);
            }
          },
        },
      ],
    });
    alert.present();
  }

  async duplicate() {
    const alert = await this.alertCtrl.create({
      header: 'Duplicate custom field?',
      subHeader: `You can confirm details before you save.`,
      buttons: [
        {
          text: 'Cancel',
        },
        {
          text: 'Duplicate',
          handler: () => {
            this.field.id = undefined;
            this.id = undefined;
            setTimeout(() => {
              this.field.label = `Copy of ${this.field.label}`;
            }, 500);
          },
        },
      ],
    });
    alert.present();
  }
}
